import { applyActionCode, confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth';
import React, { useEffect, useState } from 'react';
import { auth } from '../utils/firebase';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Logo } from '../components/logo';
import { CircleCheck, CircleX, Eye, EyeClosed, Key, Mail } from 'lucide-react';
import { LANDING_PATH, LOGIN_PATH } from '../App';
import toast from 'react-hot-toast';

const ActionHandler = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [mode, setMode] = useState("");
    const [oobCode, setOobCode] = useState("");
    const [loading, setLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState(null);
    // For reset password
    const [email, setEmail] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [passwordVisibility, setPasswordVisibility] = useState(false);
    // For email verification
    const [successMessage, setSuccessMessage] = useState("");
    const [isError, setIsError] = useState(false);

    const tooglePasswordVisibility = () => {
        setPasswordVisibility(!passwordVisibility);
    }

    const handlePasswordReset = async () => {
        try {
            if (newPassword.length < 8) {
                return toast.error("Password must be at least 8 characters long.");
            }
            if (newPassword !== confirmPassword) {
                return toast.error("Passwords do not match.");
            }
            setLoading(true);
            toast.promise(confirmPasswordReset(auth, oobCode, newPassword), {
                loading: "Resetting password...",
                success: () => {
                    navigate(LOGIN_PATH);
                    return "Password reset successfully. Please login with your new password.";
                },
                error: "Something went wrong. Please try again later."
            });
        } catch (error) {
            setErrorMessage("Something went wrong. Please try again later.");
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        (async () => {
            setLoading(true);
            const queryParams = new URLSearchParams(location.search);
            const mode = queryParams.get("mode");
            const oobCode = queryParams.get("oobCode");
            setMode(mode);
            setOobCode(oobCode);
            if (!mode || !oobCode) {
                navigate("/404");
                return;
            }
            if (mode === "resetPassword") {
                try {
                    const userEmail = await verifyPasswordResetCode(auth, oobCode);
                    setEmail(userEmail);
                } catch (error) {
                    setErrorMessage("Something went wrong. Please try again later.");
                } finally {
                    setLoading(false);
                }
            } else if (mode === "verifyEmail") {
                try {
                    await applyActionCode(auth, oobCode);
                    setSuccessMessage("Email verified successfully.");
                } catch (error) {
                    setIsError(true);
                    setErrorMessage("Something went wrong. Please try again later.");
                } finally {
                    setLoading(false);
                }
            } else {
                setErrorMessage("Something went wrong. Unusual action detected.");
                setLoading(false);
            }
        })();
    }, [location, navigate]);

    if (loading && (!email && mode === "resetPassword") && !successMessage) {
        return (
            <main className='page-size flex-center'>
                <div className='loading loading-md loading-bars' />
            </main>
        );
    }

    if (errorMessage) {
        return (
            <main className='page-size flex-center p-4'>
                <div className="flex-center p-8 gap-2 border w-full max-w-md">
                    <div className="flex flex-row gap-4 h-12 items-center">
                        <Logo className={"h-full object-contain translate-y-1"} />
                        <h1 className='text-xl font-bold text-nowrap'>
                            {mode === "resetPassword" ? "Reset Password" : "Email Verification"}
                        </h1>
                    </div>
                    <div className='w-full divider m-0' />
                    <p className='text-center leading-1 inline-flex items-center w-full mb-2'>
                        <CircleX className='inline pr-1 text-error' /> {errorMessage}
                    </p>
                    <Link to={LANDING_PATH} className="w-full btn btn-outline">
                        Back to Home
                    </Link>
                </div>
            </main>
        );
    }

    if (mode === "verifyEmail") {
        return (
            <main className='page-size flex-center p-4'>
                <div className="flex-center p-8 gap-2 border w-full max-w-md">
                    <div className="flex flex-row gap-4 h-12 items-center">
                        <Logo className={"h-full object-contain translate-y-1"} />
                        <h1 className='text-xl font-bold text-nowrap'>
                            Email Verification
                        </h1>
                    </div>
                    <div className='w-full divider m-0' />
                    <p className='text-center leading-1 inline-flex items-center w-full mb-2'>
                        {
                            isError
                                ? <CircleX className='inline pr-1 text-error' />
                                : <CircleCheck className='inline pr-1 text-success' />
                        }
                        {successMessage}
                    </p>
                    {
                        isError ? (
                            <Link to={LANDING_PATH} className="w-full btn btn-outline">
                                Back to Home
                            </Link>
                        ) : (
                            <Link to={LOGIN_PATH} className="w-full btn btn-primary font-bold">
                                Login Now
                            </Link>
                        )
                    }
                </div>
            </main>
        );
    }

    // Default: reset password UI
    return (
        <main className='page-size flex-center p-4'>
            <div className="flex-center p-8 gap-2 border w-full max-w-md">
                <div className="flex flex-row gap-4 h-12 items-center">
                    <Logo className={"h-full object-contain translate-y-1"} />
                    <h1 className='text-xl font-bold text-nowrap'>
                        Reset Password
                    </h1>
                </div>
                <div className='w-full divider m-0' />
                <label className="input input-bordered flex items-center gap-2 w-full">
                    <Mail />
                    <input type="text" className="pl-1 grow" placeholder={"Email"} value={email} disabled />
                </label>
                <label className="input input-bordered flex items-center gap-2 w-full relative">
                    <Key />
                    <input
                        type={passwordVisibility ? "text" : "password"}
                        className="w-full pl-1 grow"
                        placeholder={"New Password"}
                        value={newPassword}
                        onChange={(e) => { setNewPassword(e.target.value) }}
                    />
                    <button className='btn btn-square btn-ghost absolute top-0 right-0' onClick={tooglePasswordVisibility} type="button">
                        {passwordVisibility ? <Eye /> : <EyeClosed />}
                    </button>
                </label>
                <label className="input input-bordered flex items-center gap-2 w-full relative">
                    <Key />
                    <input
                        type={passwordVisibility ? "text" : "password"}
                        className="w-full pl-1 grow"
                        placeholder={"Confirm Password"}
                        value={confirmPassword}
                        onChange={(e) => { setConfirmPassword(e.target.value) }}
                    />
                    <button className='btn btn-square btn-ghost absolute top-0 right-0' onClick={tooglePasswordVisibility} type="button">
                        {passwordVisibility ? <Eye /> : <EyeClosed />}
                    </button>
                </label>
                <button onClick={handlePasswordReset} className="w-full btn btn-primary" disabled={loading} type="button">
                    {loading ? <span className="loading loading-spinner" /> : "Reset Password"}
                </button>
                <Link to={LOGIN_PATH} className="w-full btn btn-outline">
                    Back to Login
                </Link>
            </div>
        </main>
    );
}

export default ActionHandler; 